import { useEffect, useState } from "react";
import { Loader2, CheckCircle2, Circle, Lock, Bot, ArrowUpCircle, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const STAGE_TONE = {
  research: "bg-slate-100 text-slate-600", preparing: "bg-slate-100 text-slate-600",
  ready_to_apply: "bg-blue-50 text-[#1a5fd0]", submitted: "bg-blue-50 text-[#1a5fd0]",
  under_review: "bg-blue-50 text-[#1a5fd0]", security_review: "bg-amber-50 text-amber-700",
  contract: "bg-amber-50 text-amber-700", sandbox: "bg-indigo-50 text-indigo-600",
  production_review: "bg-[#b0862f]/10 text-[#b0862f]", active: "bg-emerald-50 text-emerald-700",
  blocked: "bg-red-50 text-red-600",
};
const LEVEL_TONE = { L1: "bg-slate-100 text-slate-600", L2: "bg-blue-50 text-[#1a5fd0]", L3: "bg-indigo-50 text-indigo-600", L4: "bg-[#b0862f]/10 text-[#b0862f]" };

export default function ProviderCommand() {
  const [d, setD] = useState(null);
  const load = () => api.get("/owner/providers/pipeline").then(({ data }) => setD(data)).catch(() => setD(null));
  useEffect(() => { load(); }, []);

  const setStage = async (key, stage) => { await api.put(`/owner/providers/${key}/stage`, { stage }); load(); };
  const toggle = async (key, item, done) => { await api.put(`/owner/providers/${key}/stage`, { checklist_item: item, done }); load(); };
  const requestProd = async (key) => {
    await api.post(`/owner/providers/${key}/request-production`);
    toast.success("Production activation sent to Approvals Center");
  };

  if (!d) return <div className="flex h-64 items-center justify-center"><Loader2 className="animate-spin text-[#1a5fd0]" /></div>;

  return (
    <div data-testid="provider-command">
      <h1 className="font-head text-2xl font-extrabold text-slate-900">Provider Command</h1>
      <p className="mt-1 text-[13px] text-slate-500">Manage financial-data provider credentialing from research to production. A provider only goes live when credentials are configured, the checklist is complete, and production is approved.</p>

      <div className="mt-5 space-y-4">
        {d.providers.map((p) => (
          <div key={p.key} className="card-spotlight rounded-2xl border border-slate-200 bg-white p-4 transition-all hover:border-slate-300" data-testid={`provider-${p.key}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-head text-[15px] font-bold text-slate-900">{p.name}</span>
                  <span className="font-mono text-[10px] uppercase tracking-wider text-slate-400">{p.category}</span>
                </div>
                <div className="mt-1 flex items-center gap-2 text-[12px]">
                  <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 font-mono text-[10px] font-bold uppercase ${p.keys_configured ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                    <KeyRound size={11} /> {p.keys_configured ? "Credentials set" : "No credentials"}
                  </span>
                  <span className="text-slate-400">{p.credentialing}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <select value={p.stage} onChange={(e) => setStage(p.key, e.target.value)} data-testid={`stage-select-${p.key}`}
                  className={`rounded-lg border-0 px-2.5 py-1.5 font-mono text-[10px] font-bold uppercase tracking-wider ${STAGE_TONE[p.stage]}`}>
                  {d.stages.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                </select>
                <button onClick={() => requestProd(p.key)} disabled={!p.production_ready} data-testid={`request-prod-${p.key}`}
                  title={p.production_ready ? "Request production activation" : `Blocked: ${p.missing_for_production.join(", ")}`}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-[#b0862f] px-3 py-1.5 font-head text-[12px] font-bold text-white disabled:opacity-40">
                  <ArrowUpCircle size={13} /> Request Production
                </button>
              </div>
            </div>
            <div className="mt-3 grid gap-1.5 sm:grid-cols-2">
              {p.checklist.map((c) => (
                <button key={c.item} onClick={() => toggle(p.key, c.item, !c.done)} data-testid={`check-${p.key}-${c.item}`}
                  className="flex items-center gap-2 rounded-lg px-2 py-1 text-left text-[13px] hover:bg-slate-50">
                  {c.done ? <CheckCircle2 size={15} className="text-emerald-600" /> : <Circle size={15} className="text-slate-300" />}
                  <span className={c.done ? "text-slate-700" : "text-slate-500"}>{c.item}</span>
                </button>
              ))}
            </div>
            {!p.production_ready && <div className="mt-2 flex items-center gap-1.5 text-[11px] text-amber-600"><Lock size={12} /> Production blocked until: {p.missing_for_production.join(", ")}</div>}
          </div>
        ))}
      </div>

      <h2 className="mt-8 font-head text-lg font-extrabold text-slate-900">AI credentialing workforce</h2>
      <p className="mt-0.5 text-[13px] text-slate-500">Agents operate under strict permission levels. High-risk actions (attestations, production) always require a human.</p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2" data-testid="ai-agents">
        {d.ai_agents.map((a) => (
          <div key={a.name} className="card-spotlight rounded-2xl border border-slate-200 bg-white p-4 transition-all hover:border-slate-300">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 font-head text-[14px] font-bold text-slate-900"><Bot size={15} className="text-[#1a5fd0]" /> {a.name}</div>
              <span className={`rounded-md px-2 py-0.5 font-mono text-[10px] font-bold ${LEVEL_TONE[a.level]}`}>{a.level}</span>
            </div>
            <p className="mt-1.5 text-[12px] text-slate-500">{a.desc}</p>
            <div className="mt-2 font-mono text-[10px] uppercase tracking-wider text-slate-400">{a.role}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
